import { useToast } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { useDeleteOrgGroupMembers } from '../api/useDeleteOrgGroupMembers';
import { OrgGroupMember } from '../api/useOrgGroupMembersList';

interface UseRemoveMembersParams {
  groupId: string;
  onSuccess?: () => void;
}

const getRemovedFromGroupSuccess = (count: number): string =>
  count === 1 ? 'User removed from the group' : 'Users removed from the group';

const getRemovedFromGroupError = (count: number): string =>
  count === 1
    ? 'Failed to remove user from the group'
    : 'Failed to remove users from the group';

export const useRemoveMembers = ({
  groupId,
  onSuccess,
}: UseRemoveMembersParams) => {
  const toast = useToast();
  const [membersToRemove, setMembersToRemove] = useState<
    string[] | OrgGroupMember
  >([]);
  const [isOpen, setIsOpen] = useState(false);
  const { mutateAsync: deleteMembers, isLoading } =
    useDeleteOrgGroupMembers(groupId);

  const handleOpen = useCallback((members: string[] | OrgGroupMember) => {
    setMembersToRemove(members);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    const memberIds = Array.isArray(membersToRemove)
      ? membersToRemove
      : [membersToRemove.id];

    if (memberIds.length === 0) return;

    try {
      await deleteMembers(memberIds);
      toast({
        status: 'success',
        description: getRemovedFromGroupSuccess(memberIds.length),
      });
      setIsOpen(false);
      onSuccess?.();
    } catch {
      toast({
        status: 'error',
        description: getRemovedFromGroupError(memberIds.length),
      });
    }
  }, [deleteMembers, membersToRemove, toast, onSuccess]);

  return {
    isOpen,
    membersToRemove,
    handleOpen,
    handleClose,
    handleConfirm,
    isLoading,
  };
};
